import { Link } from "react-router";
import { Badge, Card, LinkButton } from "../../ui/primitives";
import { useLandingContent } from "./lp-content/context";

export function LandingPage() {
	const c = useLandingContent();
	return (
		<>
			<section
				data-testid="lp-hero"
				className="border-b border-ink-200 bg-gradient-to-b from-brand-50 to-white"
			>
				<div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:py-24">
					<div>
						<Badge tone="brand">{c.hero.badge}</Badge>
						<h1 className="mt-4 text-[34px] font-bold leading-tight tracking-tight text-ink-900 sm:text-[42px]">
							{c.hero.title.map((line, i) => (
								<span key={line}>
									{i > 0 && <br />}
									{line}
								</span>
							))}
						</h1>
						<p className="mt-4 max-w-xl text-[16px] leading-relaxed text-ink-700">
							{c.hero.description}
						</p>
						<div className="mt-7 flex flex-wrap gap-3">
							<LinkButton to="/signup">{c.hero.primary}</LinkButton>
							<LinkButton to="/demo?api=mock" variant="secondary">
								{c.hero.secondary}
							</LinkButton>
						</div>
						<p className="mt-4 text-[13px] text-ink-500">{c.hero.note}</p>
					</div>
					<div className="ag-card overflow-hidden shadow-sm">
						<div className="border-b border-ink-100 bg-ink-50 px-4 py-2 text-[12px] font-semibold text-ink-500">
							{c.hero.chatLabel}
						</div>
						<div className="space-y-3 px-4 py-4">
							{c.hero.chat.map((m, i) => (
								<div key={m.text}>
									{i === 1 && (
										<div className="mb-3 flex flex-wrap gap-1.5 pl-2">
											{c.hero.choices.map((x) => (
												<span
													key={x}
													className="rounded-full border border-brand-300 bg-white px-2.5 py-1 text-[12px] text-brand-700"
												>
													{x}
												</span>
											))}
										</div>
									)}
									<ChatBubble speaker={m.speaker}>{m.text}</ChatBubble>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>
			<section data-testid="lp-difference" className="mx-auto max-w-6xl px-4 py-16 lg:px-8">
				<h2 className="text-[24px] font-bold tracking-tight text-ink-900">{c.difference.title}</h2>
				<div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{c.difference.items.map((x) => (
						<Card key={x.title}>
							<p className="text-[15px] font-bold text-ink-900">{x.title}</p>
							<p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-600">{x.body}</p>
						</Card>
					))}
				</div>
			</section>
			<section data-testid="lp-how" className="border-y border-ink-200 bg-ink-50">
				<div className="mx-auto max-w-6xl px-4 py-16 lg:px-8">
					<h2 className="text-[24px] font-bold tracking-tight text-ink-900">{c.how.title}</h2>
					<ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
						{c.how.steps.map((x, i) => (
							<li key={x.title} className="ag-card px-4 py-4">
								<span className="inline-flex size-7 items-center justify-center rounded-full bg-brand-600 text-[13px] font-bold text-white">
									{i + 1}
								</span>
								<p className="mt-2 text-[14.5px] font-bold text-ink-900">{x.title}</p>
							</li>
						))}
					</ol>
				</div>
			</section>
			<section data-testid="lp-industries" className="mx-auto max-w-6xl px-4 py-16 lg:px-8">
				<div className="flex flex-wrap items-end justify-between gap-3">
					<div>
						<h2 className="text-[24px] font-bold tracking-tight text-ink-900">
							{c.industries.title}
						</h2>
						<p className="mt-1 text-[14px] text-ink-600">{c.industries.description}</p>
					</div>
					<Link
						to="/solutions"
						className="text-[14px] font-semibold text-brand-600 hover:underline"
					>
						{c.industries.allLabel}
					</Link>
				</div>
				<div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{c.industries.items.map((x) => (
						<Link
							key={x.key}
							to={`/solutions/${x.key}`}
							className="ag-card block px-4 py-4 transition-colors hover:border-brand-300"
						>
							<div className="flex items-center gap-2">
								<span className="text-[15px] font-bold text-ink-900">{x.name}</span>
								{x.priority && <Badge tone="brand">{c.industries.priorityLabel}</Badge>}
							</div>
							<p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-600">{x.tagline}</p>
							<p className="mt-2 text-[12px] text-ink-500">
								{c.industries.scopeLabel}
								{x.scope}
							</p>
						</Link>
					))}
				</div>
			</section>
			<section data-testid="lp-pricing" className="border-t border-ink-200 bg-ink-50">
				<div className="mx-auto max-w-6xl px-4 py-12 text-center lg:px-8">
					<h2 className="text-[24px] font-bold text-ink-900">{c.pricing.title}</h2>
					<p className="mt-3 text-[16px] text-ink-600">{c.pricing.status}</p>
				</div>
			</section>
			<section data-testid="lp-trust" className="border-t border-ink-200">
				<div className="mx-auto max-w-6xl px-4 py-12 lg:px-8">
					<h2 className="text-[20px] font-bold text-ink-900">{c.trust.title}</h2>
					<div className="mt-4 grid gap-4 sm:grid-cols-3">
						<p>
							{c.trust.operatorLabel}: {c.trust.operator}
						</p>
						<nav aria-label={c.trust.title}>
							{c.trust.legalLinks.map((x) => (
								<Link key={x.href} to={x.href} className="mr-3 text-brand-600 hover:underline">
									{x.label}
								</Link>
							))}
						</nav>
						<p>{c.trust.aiNotice}</p>
					</div>
				</div>
			</section>
			<section data-testid="lp-cta" className="border-t border-ink-200 bg-ink-900">
				<div className="mx-auto max-w-3xl px-4 py-16 text-center lg:px-8">
					<h2 className="text-[26px] font-bold tracking-tight text-white">{c.cta.title}</h2>
					<p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-ink-300">
						{c.cta.description}
					</p>
					<div className="mt-7 flex flex-wrap justify-center gap-3">
						<LinkButton to="/signup">{c.cta.primary}</LinkButton>
						<LinkButton to="/contact" variant="secondary">
							{c.cta.secondary}
						</LinkButton>
					</div>
				</div>
			</section>
		</>
	);
}

function ChatBubble({ speaker, children }: { speaker: "agent" | "user"; children: string }) {
	const agent = speaker === "agent";
	return (
		<div className={agent ? "flex gap-2" : "flex justify-end"}>
			{agent && (
				<span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white">
					A
				</span>
			)}
			<p
				className={
					agent
						? "rounded-2xl rounded-tl-sm bg-ink-100 px-3 py-2 text-[13.5px] leading-relaxed text-ink-800"
						: "rounded-2xl rounded-tr-sm bg-brand-600 px-3 py-2 text-[13.5px] leading-relaxed text-white"
				}
			>
				{children}
			</p>
		</div>
	);
}
